/** 인증 상세 창 사진 칸(가로 640px)과 1:1인 원본 크기. 세로로 긴 사진은 높이도 제한 */
export const CHALLENGE_PHOTO_MAX_WIDTH = 640;
export const CHALLENGE_PHOTO_MAX_HEIGHT = 1280;
export const CHALLENGE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('사진을 읽을 수 없어요. 다른 사진을 선택해 주세요.'));
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * 가로 640px(세로 1280px 이내)로 줄이고 WebP(미지원 브라우저는 JPEG)로 압축. 2MB를 넘으면 품질을 낮춰 다시 인코딩.
 * img 요소는 EXIF 방향을 반영해 그리므로 휴대폰 세로 사진도 바로 선다.
 */
export async function compressChallengePhoto(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;
    if (!w0 || !h0) throw new Error('사진 크기를 확인할 수 없어요.');
    const scale = Math.min(1, CHALLENGE_PHOTO_MAX_WIDTH / w0, CHALLENGE_PHOTO_MAX_HEIGHT / h0);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w0 * scale));
    canvas.height = Math.max(1, Math.round(h0 * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('사진을 처리할 수 없어요.');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const qualities = [0.7, 0.6, 0.5, 0.4];
    let mime = 'image/webp';
    for (const q of qualities) {
      let blob = await canvasToBlob(canvas, mime, q);
      if (blob && blob.type !== 'image/webp' && mime === 'image/webp') {
        mime = 'image/jpeg';
        blob = await canvasToBlob(canvas, mime, q + 0.05);
      }
      if (blob && blob.size <= CHALLENGE_PHOTO_MAX_BYTES) return blob;
    }
    throw new Error('사진이 너무 커요. 다른 사진을 선택해 주세요.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 교사 확인 카드의 사진 칸(4:3, 가로 약 240px)에 맞춘 썸네일 */
export const CHALLENGE_PHOTO_THUMB_WIDTH = 320;
export const CHALLENGE_PHOTO_THUMB_HEIGHT = 240;

/** 카드 칸과 같은 4:3으로 가운데를 잘라 320×240 썸네일을 만든다. 원본과 같은 형식(WebP/JPEG) */
export async function createChallengePhotoThumb(original: Blob): Promise<Blob | null> {
  const url = URL.createObjectURL(original);
  try {
    const img = await loadImage(url);
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;
    if (!w0 || !h0) return null;
    const tw = CHALLENGE_PHOTO_THUMB_WIDTH;
    const th = CHALLENGE_PHOTO_THUMB_HEIGHT;
    const srcRatio = w0 / h0;
    const dstRatio = tw / th;
    const sw = srcRatio > dstRatio ? h0 * dstRatio : w0;
    const sh = srcRatio > dstRatio ? h0 : w0 / dstRatio;
    const canvas = document.createElement('canvas');
    canvas.width = Math.min(tw, Math.round(sw));
    canvas.height = Math.round(canvas.width / dstRatio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, (w0 - sw) / 2, (h0 - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
    const mime = original.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp';
    const blob = await canvasToBlob(canvas, mime, 0.6);
    return blob && blob.type === mime ? blob : null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
